import React, { useEffect, useRef, useState } from "react";
import {
  LockClosedIcon,
  PencilSquareIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import type { DateRangeFilter } from "../types";
import type { PersistedFilterState } from "../utils/filterPersistence";

import type { SavedView } from "../utils/savedViews";
import { resolveView, toViewSlice, viewSignature } from "../utils/savedViews";
import { useSavedViews } from "../hooks/useSavedViews";
import { windowLabel } from "../utils/dateWindow";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { SaveViewPopover } from "./SaveViewPopover";
import type { WindowMode } from "./SaveViewPopover";

interface SavedViewTabsProps {
  /** The current filter config, to save and to detect the active tab. */
  current: PersistedFilterState;
  /** Load a saved view's config into the page. */
  onApply: (state: PersistedFilterState) => void;
  /**
   * sessionStorage namespace, so each page keeps its own view list — required
   * rather than defaulted so a second page can't silently share this one's.
   */
  namespace: string;
  /** What the current window resolves to — what "fixed" would freeze. */
  resolvedRange?: DateRangeFilter;
}

/**
 * Saved-view tabs above the filters: quick jumps between named filter +
 * group-by + window configs (see utils/savedViews). "Save view" snapshots the
 * current data slice; a tab loads it, keeping the metric you're currently on;
 * the active tab is the one matching the current slice. Session-scoped per page
 * namespace and impersonation domain.
 *
 * A tab can also be edited in place — renamed, or pointed at the slice on
 * screen — which keeps its id, so a link someone else is holding still resolves
 * to the view they were given. Every view in the strip is the caller's own (the
 * server only lists yours, and hides these controls entirely while
 * impersonating), so there is no per-tab permission to check here.
 */
export function SavedViewTabs({
  current,
  onApply,
  namespace,
  resolvedRange,
}: SavedViewTabsProps) {
  const { views, error, writeError, readOnly, create, update, remove } =
    useSavedViews(namespace);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // A write now decides whether its popover closes, so it has to be awaited —
  // which leaves a window a second click on the same control can land in: a
  // repeated save would add the view twice, and a repeated delete would report
  // a 404 for a view that is already gone. Keyed by the write rather than by
  // its target, so only a genuine repeat is dropped: deleting a view while its
  // own rename is in flight is a different write, and so is saving a second
  // name after the first one stalled.
  const inFlight = useRef(new Set<string>());
  const popRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLDivElement>(null);
  useClickOutside(popRef, () => setSaving(false), saving);
  useClickOutside(editRef, () => setEditingId(null), editingId !== null);

  const currentSig = viewSignature(current);

  /**
   * The page's data slice as a view would store it. "Fixed" snapshots what the
   * window resolves to *now*, turning a rolling window into a baseline that
   * will still show this period next quarter.
   */
  const pageSlice = (mode: WindowMode): PersistedFilterState => {
    // A view is a data slice: filters, criteria, score, group-by and window.
    // toViewSlice drops the metric, which is display state.
    const slice = toViewSlice(current);
    return {
      ...slice,
      dateWindow:
        mode === "fixed" && resolvedRange
          ? {
              kind: "absolute",
              start: resolvedRange.start_date,
              end: resolvedRange.end_date,
            }
          : slice.dateWindow,
    };
  };

  /**
   * Run the write identified by `key`, and dismiss its popover only once it has
   * landed. A failure leaves the popover open with the name still in the field,
   * and `writeError` says what didn't happen — dismissing regardless is what
   * made a rejected save indistinguishable from a successful one.
   */
  const run = async (
    key: string,
    write: () => Promise<boolean>,
    landed?: () => void,
  ) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    try {
      if (await write()) landed?.();
    } finally {
      inFlight.current.delete(key);
    }
  };

  const handleSave = (name: string, mode: WindowMode) =>
    // A new view has no id to key on; its name is what a repeat would repeat.
    void run(
      `create:${name}`,
      () => create(name, pageSlice(mode)),
      () => setSaving(false),
    );

  const handleEdit = (
    view: SavedView,
    name: string,
    mode: WindowMode,
    replaceSlice: boolean,
  ) =>
    void run(
      `update:${view.id}`,
      // Not replacing means a rename: the view keeps the slice it already holds.
      () => update(view.id, name, replaceSlice ? pageSlice(mode) : toViewSlice(view)),
      // Only this view's popover: a slow edit must not close one the user has
      // since opened on another tab, taking what they typed with it.
      () => setEditingId((id) => (id === view.id ? null : id)),
    );

  const handleDelete = (id: string) => void run(`delete:${id}`, () => remove(id));

  // A failed write takes the line: it is the more recent thing to have gone
  // wrong, and unlike a failed load it is the user's own action.
  const notice =
    writeError ??
    (error ? "Couldn't reach the server — showing your last saved views." : null);

  return (
    <div className="flex flex-col" data-testid="saved-views">
      {/* Always mounted: a live region inserted with its text already in it is
          typically not announced, which would leave exactly the users who most
          need to be told that a write failed as the ones who aren't. */}
      <div
        role="status"
        className={
          notice
            ? `text-xs mb-1 ${writeError ? "text-destructive" : "text-muted-foreground"}`
            : undefined
        }
      >
        {notice}
      </div>
      <div
        className="flex items-center gap-1 flex-wrap border-b border-border"
        role="tablist"
        data-testid="saved-view-tabs"
      >
      {views.map((v) => {
        const active = viewSignature(v) === currentSig;
        // A baseline quietly serving last quarter's data is the footgun the
        // original window exclusion avoided, reintroduced here on purpose — so
        // it has to be visible.
        const frozen = v.dateWindow?.kind === "absolute";
        return (
          <div
            key={v.id}
            ref={editingId === v.id ? editRef : undefined}
            className={`group relative flex items-center border-b-2 -mb-px ${
              active ? "border-primary" : "border-transparent"
            }`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              title={frozen ? `Fixed window · ${windowLabel(v.dateWindow)}` : undefined}
              onClick={() => onApply(resolveView(v, current))}
              className={`py-2 pl-3 pr-1 text-sm ${
                active
                  ? "text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {v.name}
              {frozen && (
                <LockClosedIcon
                  className="inline-block h-3 w-3 ml-1 -mt-0.5 opacity-70"
                  aria-hidden="true"
                />
              )}
            </button>
            {!readOnly && (
            <>
            {/* The pencil stays visible while its popover is open, so the
                popover keeps a visible anchor once the pointer leaves the tab. */}
            <button
              type="button"
              aria-label={`Edit view ${v.name}`}
              aria-haspopup="dialog"
              onClick={() => setEditingId((id) => (id === v.id ? null : v.id))}
              className={`px-1 text-muted-foreground hover:text-foreground focus:opacity-100 transition-opacity ${
                editingId === v.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={`Delete view ${v.name}`}
              onClick={() => handleDelete(v.id)}
              className="pr-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
            </>
            )}
            {editingId === v.id && (
              <SaveViewPopover
                dateWindow={current.dateWindow}
                resolvedRange={resolvedRange}
                editing={{ name: v.name, sliceDiffers: !active }}
                onSave={(name, mode, replaceSlice) =>
                  handleEdit(v, name, mode, replaceSlice)
                }
                onCancel={() => setEditingId(null)}
              />
            )}
          </div>
        );
      })}

      {!readOnly && (
      <div className="relative" ref={popRef}>
        <button
          type="button"
          onClick={() => setSaving((s) => !s)}
          aria-haspopup="dialog"
          className="inline-flex items-center gap-1 py-2 px-2 text-sm font-medium text-primary hover:underline"
        >
          <PlusIcon className="h-4 w-4" /> Save view
        </button>
        {saving && (
          <SaveViewPopover
            dateWindow={current.dateWindow}
            resolvedRange={resolvedRange}
            onSave={handleSave}
            onCancel={() => setSaving(false)}
          />
        )}
      </div>
      )}
      </div>
    </div>
  );
}
